/**
 * Debounced Value Hook
 *
 * Delays updating a value until the user stops changing it for a specified time.
 * This prevents expensive operations (like context window calculations) from
 * running on every keystroke, improving typing performance.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";

/**
 * Returns a debounced version of the value
 * The value will only update after `delay` milliseconds of inactivity
 */
export function useDebouncedValue(value, t0) {
  const $ = _c(4);
  const delay = t0 === undefined ? 300 : t0;
  const [debouncedValue, setDebouncedValue] = useState(value);
  let t1;
  let t2;
  if ($[0] !== delay || $[1] !== value) {
    t1 = () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    };
    t2 = [value, delay];
    $[0] = delay;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return debouncedValue;
}